import { defineStore } from 'pinia'
import { ref } from 'vue'

export type AccountContextOption = {
  label: string
  value: string | number
  reseller_id?: string | number | null
  timezone?: string
}

/**
 * Cached account dropdown options and display names.
 * Selection lives in the URL (?account_id=) — see shared/scope/listScope.ts.
 */
export const useAccountContextStore = defineStore('accountContext', () => {
  const accountOptions = ref<AccountContextOption[]>([])
  const isLoadingAccounts = ref(false)
  const optionsLoadedForResellerId = ref<string | number | undefined | null>(null)
  const optionsLoadedForResource = ref<string | undefined>(undefined)
  const accountNames = ref<Record<string, string>>({})

  function setAccountOptions(
    options: AccountContextOption[],
    resellerId: string | number | undefined | null,
    resource?: string
  ) {
    accountOptions.value = options
    optionsLoadedForResellerId.value = resellerId ?? null
    optionsLoadedForResource.value = resource
  }

  function setAccountName(accountId: string | number, name: string) {
    if (!name) return
    accountNames.value[String(accountId)] = name
  }

  function getAccountName(accountId: string | number | undefined): string {
    if (!accountId) return ''
    return accountNames.value[String(accountId)] ?? ''
  }

  function clear() {
    accountOptions.value = []
    isLoadingAccounts.value = false
    optionsLoadedForResellerId.value = null
    optionsLoadedForResource.value = undefined
    accountNames.value = {}
  }

  return {
    accountOptions,
    isLoadingAccounts,
    optionsLoadedForResellerId,
    optionsLoadedForResource,
    accountNames,
    setAccountOptions,
    setAccountName,
    getAccountName,
    clear
  }
})
